// Assistents.tsx
import React, { useEffect, useState } from "react";
import { Row, Col, Typography, Grid, App, FloatButton, Layout, theme } from "antd";
import { Header } from "antd/es/layout/layout";
import { MessageOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AppHeader from "../layout/AppHeader";

const { Title, Paragraph } = Typography;
const WIDGET_SRC = "https://unpkg.com/@elevenlabs/convai-widget-embed";

// Tipado opcional del web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "elevenlabs-convai": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & { "agent-id"?: string };
    }
  }
}

async function loadConvaiScript(): Promise<void> {
  // Si ya existe el custom element, no recargar
  if ("customElements" in window && customElements.get("elevenlabs-convai")) return;

  // ¿script ya agregado?
  const existing = document.querySelector(`script[src="${WIDGET_SRC}"]`) as HTMLScriptElement | null;
  if (existing) {
    if (existing.dataset.loaded === "true") return;
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("load-error")), { once: true });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = WIDGET_SRC;
    s.async = true;
    s.type = "text/javascript";
    s.addEventListener("load", () => { s.dataset.loaded = "true"; resolve(); });
    s.addEventListener("error", () => reject(new Error("load-error")));
    document.body.appendChild(s);
  });
}

const Assistents: React.FC = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();

  const [enabled, setEnabled] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [widgetEl, setWidgetEl] = useState<HTMLElement | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.resolvedLanguage || i18n.language);

  // Cargar script sólo al habilitar
  useEffect(() => {
    let alive = true;
    if (enabled && !scriptReady) {
      loadConvaiScript()
        .then(() => alive && setScriptReady(true))
        .catch(() => {
          message.error(t("assistants.loadError"));
          if (alive) setEnabled(false);
        });
    }
    return () => { alive = false; };
  }, [enabled, scriptReady, message, t]);

  // Montar/desmontar el web component
  useEffect(() => {
    if (enabled && scriptReady && !widgetEl) {
      const existing = document.querySelector("elevenlabs-convai") as HTMLElement | null;
      if (existing) {
        setWidgetEl(existing);
      } else {
        const el = document.createElement("elevenlabs-convai");
        el.setAttribute("agent-id", "lymVFHlT0HDBAQs9P0BP");
        document.body.appendChild(el); // el widget flota por sí solo
        setWidgetEl(el);
      }
    }
    if (!enabled && widgetEl) {
      try { widgetEl.remove(); } catch { }
      setWidgetEl(null);
    }
  }, [enabled, scriptReady, widgetEl]);

  // Cerrar al hacer click fuera o presionar Esc
  useEffect(() => {
    if (!enabled || !widgetEl) return;

    // Evitar que el click que abre cierre inmediatamente
    const attach = () => {
      const onPointerDown = (ev: Event) => {
        // Si el click proviene del widget (incluye shadow DOM), no cerrar
        const path = (ev as any).composedPath?.() as EventTarget[] | undefined;
        if (path && path.includes(widgetEl)) return;
        setEnabled(false);
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setEnabled(false);
      };

      document.addEventListener("pointerdown", onPointerDown, true);
      document.addEventListener("keydown", onKeyDown);

      return () => {
        document.removeEventListener("pointerdown", onPointerDown, true);
        document.removeEventListener("keydown", onKeyDown);
      };
    };

    const cleanup = attach();
    return cleanup;
  }, [enabled, widgetEl]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => setCurrentLanguage(lng);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const floatSize = isMobile ? 44 : 56;

  return (
   <Layout data-language={currentLanguage} style={{ display: 'flex', flexDirection: 'column', minHeight: '100svh', background: token.colorBgBase }}>
      <AppHeader position={isMobile ? 'fixed' : 'sticky'} />

      <Row style={{ height: "100%" }}>
        <Col span={24} style={{ height: "100%" }}>
          <div style={{ overflowY: "auto", height: "100%", padding: 25, paddingBottom: 80 }}>
            <Title level={2} style={{marginTop: '50px'}}>{t("assistants.title")}</Title>
            <Paragraph style={{ fontSize: 20, textAlign: "justify", whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {t("assistants.description")}
            </Paragraph>
          </div>
        </Col>
      </Row>

      {!enabled && (
        <FloatButton
          icon={<MessageOutlined />}
          type="primary"
          tooltip={t("assistants.openAssistant")}
          onClick={() => setEnabled(true)}
          aria-label={t("assistants.openAssistant")}
          style={{
            width: floatSize,
            height: floatSize,
            right: 16,
            bottom: 16,
            marginBottom: "env(safe-area-inset-bottom)",
          }}
        />
      )}
    </Layout>
  );

};

export default Assistents;
